// entities/construct.ts
import { _manualUpdate, fromNetwork, upgradeMark } from "../symbols";
import { EntityStore } from "../client/entityStore";
import { baseqlMetaData } from "../metadata/metaDataStore";
import MapBehaviorSubject from "@adornis/base/rxjs-extension/reactives/map";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap
} from "rxjs";
import { getTypeString, isGQLScalarType } from "../baseqlTypes";
import { A } from "@adornis/base/env-info";
import { defaultCompare } from "./defaultFunctions";
var constructValue = (value, field) => {
  if (value?.[upgradeMark])
    return value;
  if (value === null || value === void 0)
    return value;
  if (field?.arrayDepth())
    return constructArrayValue(value, field, field.arrayDepth());
  const upgrade = A.isClient ? EntityStore.getAndMerge(value, field) ?? EntityStore.put(applyConstructor(value, field?.type), field) : applyConstructor(value, field?.type);
  return upgrade;
};
var applyConstructor = (value, typeFunc) => {
  const type = typeFunc?.();
  if (type?._isBaseQLEnum)
    return value;
  const scalar = Object.values(baseqlMetaData.scalars).find((s) => s.isTypeOf(type));
  if (scalar)
    return scalar.coerceValue(value);
  if (value._class !== void 0 && value._class !== type?._class) {
    const node = baseqlMetaData.entities.find((node2) => node2.type()._class === value._class);
    if (!node)
      throw new Error(`Unable to instantiate unknown entity ${value._class}. No constructor found in metaDataStore.`);
    return new (node.type())(value);
  }
  if (!type)
    throw new Error(
      `called applyConstructor without type definition and value does not contain a _class field! ${JSON.stringify(
        value
      )}`
    );
  return new type(value);
};
var toJSPrimitive = (value, typeFunc) => {
  const type = typeFunc();
  if (type._isBaseQLEnum)
    return value;
  const scalar = Object.values(baseqlMetaData.scalars).find((s) => s.isTypeOf(type));
  if (scalar?.toJSPrimitive) {
    return scalar.toJSPrimitive(value);
  }
  return value;
};
var constructArrayValue = (value, field, arrayDepth) => {
  if (value?.[upgradeMark])
    return value;
  const isScalar = isGQLScalarType(field.type);
  if (!arrayDepth) {
    if (value === void 0 || value === null)
      return value;
    if (A.isClient && !isScalar && !EntityStore.resolveKey("", value, field)) {
      throw new Error(
        `Refusing to merge array of entities without unique IDs. Please define an _id field or a static resolveID function on ${getTypeString(
          field.type
        )}`
      );
    }
    const intermediate = A.isClient ? EntityStore.getAndMerge(value, field) ?? EntityStore.put(applyConstructor(value, field.type), field) : applyConstructor(value, field.type);
    return intermediate;
  }
  const isFromNetwork = value[fromNetwork];
  const array = [];
  const workArray = [];
  const arrayState = new BehaviorSubject({ value: null, dirty: false });
  const itemMetaData = new MapBehaviorSubject();
  let metaPaused = false;
  const getMetaObservable = (visited = /* @__PURE__ */ new Set()) => {
    if (!itemMetaData.getValue().size && !isScalar)
      workArray.forEach((item) => itemMetaData.set(item, {}));
    return itemMetaData.pipe(
      filter((_) => !metaPaused),
      switchMap((metaData) => {
        const observables = isScalar ? [] : Array.from(metaData.entries()).map(([value2, metaData2]) => {
          if (value2 === _manualUpdate)
            return of({ value: null, dirty: false });
          if (!metaData2.metaObs)
            metaData2.metaObs = value2.getMetaObservable(visited);
          return metaData2.metaObs.pipe(map((valueMeta) => ({ value: null, dirty: valueMeta.some((m) => m.dirty) })));
        }).filter(Boolean);
        observables.unshift(arrayState);
        return combineLatest(observables);
      })
    );
  };
  const updateMetaData = () => {
    if (isScalar)
      return;
    metaPaused = true;
    Array.from(itemMetaData.keys()).forEach((item) => {
      if (!workArray.includes(item) && item !== _manualUpdate) {
        itemMetaData.get(item)?.metaSub?.unsubscribe();
        itemMetaData.delete(item);
      }
    });
    workArray.forEach((item) => {
      if (!itemMetaData.has(item)) {
        itemMetaData.set(item, {});
      }
    });
    metaPaused = false;
    itemMetaData.set(_manualUpdate, {});
  };
  if (!value.forEach)
    console.log("DEBUG constructArrayValue on non array value!", value, field, arrayDepth);
  value.forEach((entry, index) => {
    if (!isScalar && entry)
      entry[fromNetwork] = isFromNetwork;
    const constructed = constructArrayValue(entry, field, arrayDepth - 1);
    array[index] = constructed;
    workArray[index] = constructed;
  });
  const proxiedArray = new Proxy(workArray, {
    get: (target, name) => {
      if (name === upgradeMark)
        return true;
      if (name === "getMetaObservable") {
        return getMetaObservable;
      }
      if (name === "dirty") {
        return getMetaObservable().pipe(
          map((meta) => meta.some((m) => m.dirty)),
          distinctUntilChanged()
        );
      }
      if (name === "whenChanging") {
        return getMetaObservable().pipe(map(() => proxiedArray));
      }
      if (name === "isDirty")
        return function(recurse = true, visited = /* @__PURE__ */ new Set()) {
          const state = arrayState.getValue();
          if (state.dirty)
            return true;
          if (!isScalar && recurse)
            return workArray.some(
              (entry) => entry?.[upgradeMark] && !visited.has(entry) && entry.isDirty(recurse, visited)
            );
          return false;
        };
      if (name === "_commit")
        return async function(force = false, visited = /* @__PURE__ */ new Set()) {
          array.length = workArray.length;
          for (let i = 0; i < workArray.length; i++) {
            const entry = workArray[i];
            if (entry?.[upgradeMark] && !visited.has(entry))
              await entry._commit(force, visited);
            array[i] = entry;
          }
          updateMetaData();
          arrayState.next({ value: null, dirty: false });
        };
      if (name === "_revert")
        return function(visited) {
          metaPaused = true;
          workArray.forEach((entry) => {
            if (entry?.[upgradeMark] && !visited.has(entry))
              entry._revert(visited);
          });
          workArray.length = array.length;
          array.forEach((entry, index) => {
            workArray[index] = entry;
          });
          metaPaused = false;
          updateMetaData();
          arrayState.next({ value: null, dirty: false });
        };
      if (name === "mergeData")
        return function(data, mergeIsFromNetwork) {
          if (!data)
            return;
          if (!Array.isArray(data))
            throw new Error(`Cannot merge array with non-array data! data: ${data}, array: ${array}`);
          array.length = data.length;
          const state = arrayState.getValue();
          if (!state.dirty)
            workArray.length = data.length;
          data.forEach((entry, index) => {
            if (!isScalar)
              entry[fromNetwork] = mergeIsFromNetwork;
            const constructed = constructArrayValue(entry, field, arrayDepth - 1);
            array[index] = constructed;
            if (!state.dirty)
              workArray[index] = constructed;
          });
          updateMetaData();
          arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        };
      if (name === "toObject")
        return function(filterResolved = false, visited = /* @__PURE__ */ new Set()) {
          return workArray.map(
            (entry) => entry?.[upgradeMark] ? visited.has(entry) ? void 0 : entry.toObject(filterResolved, visited) : toJSPrimitive(entry, field.type)
          ).filter((entry) => entry !== void 0);
        };
      if (name === "validate")
        return async function(options = {}, validated = /* @__PURE__ */ new Set()) {
          for (let i = 0; i < workArray.length; i++) {
            const entry = workArray[i];
            entry?.[upgradeMark] && !validated.has(entry) && await entry.validate(options, validated);
          }
        };
      if (typeof target[name] === "function") {
        return function() {
          const result = workArray[name].apply(workArray, arguments);
          if (["copyWithin", "fill", "pop", "push", "reverse", "shift", "sort", "splice", "unshift"].includes(
            name
          )) {
            const changed = !compareArrays(array, workArray, field);
            updateMetaData();
            arrayState.next({ value: null, dirty: changed });
          }
          return result;
        };
      }
      return target[name];
    },
    set: (target, name, value2) => {
      if (accessingArrayEntries(name)) {
        workArray[name] = value2;
        updateMetaData();
        arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        return true;
      }
      if (name === "length") {
        workArray.length = value2;
        updateMetaData();
        arrayState.next({ value: null, dirty: !compareArrays(array, workArray, field) });
        return true;
      }
      target[name] = value2;
      return true;
    }
  });
  return proxiedArray;
};
var compareArrays = (array, workArray, field) => {
  if (array.length !== workArray.length)
    return false;
  return array.every((_, index) => defaultCompare(array[index], workArray[index], field));
};
var accessingArrayEntries = (index) => typeof index !== "symbol" && !isNaN(+index) && index !== "";
export {
  constructValue,
  toJSPrimitive
};
